import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Search, AlertTriangle } from 'lucide-react';
import { businessPublicService, businessTicketService } from '../../../services/businessApi';
import { useAuth } from '../../../context/AuthContext';
import '../../../styles/businessTheme.css';
import './BusinessTickets.css';

const STATUSES = ['Open', 'In Progress', 'Redirected', 'Awaiting Supplier', 'Resolved', 'Closed'];

export default function BusinessTickets() {
  const { isBusinessLead } = useAuth();
  const [plants, setPlants] = useState([]);
  const [categories, setCategories] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [filters, setFilters] = useState({ plantId: '', status: '', categoryId: '', search: '' });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    businessPublicService.listPlants().then(res => setPlants(res.data.plants));
    businessPublicService.listCategories().then(res => setCategories(res.data.categories));
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    const params = { ...filters, page };
    Object.keys(params).forEach(k => { if (!params[k]) delete params[k]; });
    const res = await businessTicketService.getAll(params);
    setTickets(res.data.tickets);
    setTotal(res.data.total);
    setPages(res.data.pages);
    setLoading(false);
  }, [filters, page]);

  useEffect(() => { load(); }, [load]);

  const updateFilter = (field) => (e) => { setPage(1); setFilters(f => ({ ...f, [field]: e.target.value })); };

  return (
    <div className="business-tickets-page biz-theme biz-page">
      <div className="page-header">
        <div>
          <h1 className="page-title">Business Support Tickets</h1>
          <p className="page-subtitle">{total} ticket{total !== 1 ? 's' : ''}</p>
        </div>
        {isBusinessLead && (
          <Link to="/business/tickets/new" className="btn btn-primary">
            <Plus size={14} /> New Ticket
          </Link>
        )}
      </div>

      <div className="business-tickets-toolbar">
        <div className="search-box">
          <Search size={14} />
          <input placeholder="Search title..." value={filters.search} onChange={updateFilter('search')} />
        </div>
        <select value={filters.plantId} onChange={updateFilter('plantId')}>
          <option value="">All plants</option>
          {plants.map(p => <option key={p._id} value={p._id}>{p.name}</option>)}
        </select>
        <select value={filters.categoryId} onChange={updateFilter('categoryId')}>
          <option value="">All categories</option>
          {categories.map(c => <option key={c._id} value={c._id}>{c.name}</option>)}
        </select>
        <select value={filters.status} onChange={updateFilter('status')}>
          <option value="">All statuses</option>
          {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>

      <div className="card business-tickets-table">
        <table>
          <thead>
            <tr>
              <th>Ticket</th>
              <th>Plant</th>
              <th>Category</th>
              <th>Supplier</th>
              <th>Status</th>
              <th>Last Activity</th>
            </tr>
          </thead>
          <tbody>
            {tickets.map(t => (
              <tr key={t._id}>
                <td>
                  <Link to={`/business/tickets/${t._id}`} className="bt-number">{t.ticketNumber}</Link>
                  <div className="bt-title">{t.title}</div>
                </td>
                <td>{t.plant?.name || '—'}</td>
                <td>{t.category?.name || '—'}</td>
                <td>{t.supplier ? `${t.supplier.name} (${t.supplier.vendorCode})` : '—'}</td>
                <td><span className={`badge badge-${t.status.toLowerCase().replace(/\s+/g, '-')}`}>{t.status}</span></td>
                <td>
                  <div className="bt-activity">
                    {new Date(t.lastActivityAt).toLocaleDateString()}
                    {t.isStale && <span className="bt-stale" title="Stale — no recent activity"><AlertTriangle size={12} /> Stale</span>}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {!loading && tickets.length === 0 && <div className="empty-state">No tickets found</div>}
      </div>

      {pages > 1 && (
        <div className="business-tickets-pagination">
          <button className="btn btn-ghost btn-sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>Previous</button>
          <span>Page {page} of {pages}</span>
          <button className="btn btn-ghost btn-sm" disabled={page >= pages} onClick={() => setPage(p => p + 1)}>Next</button>
        </div>
      )}
    </div>
  );
}
